import React from 'react';
import { Box, Typography, Avatar } from '@mui/material';
import { AttachFile } from '@mui/icons-material';
import { MailMessageSummary } from '../types';
import { MAIL_COLORS } from '../constants';
import { formatMailDate, getMailInitials, getAvatarColor } from '../utils';

interface MailListItemProps {
  message: MailMessageSummary;
  isSelected: boolean;
  onClick: () => void;
}

export const MailListItem: React.FC<MailListItemProps> = ({
  message,
  isSelected,
  onClick
}) => {
  const senderName = message.from?.name || message.from?.email || 'Неизвестный отправитель';
  const initials = getMailInitials(message.from?.name, message.from?.email);
  const avatarColor = getAvatarColor(message.from?.email || senderName);
  const formattedDate = formatMailDate(message.date);

  return (
    <Box
      onClick={onClick}
      role="button"
      tabIndex={0}
      sx={{
        p: 2,
        px: 2.5,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.75,
        cursor: 'pointer',
        borderBottom: `1px solid ${MAIL_COLORS.borderLight}`,
        bgcolor: isSelected
          ? '#eff8ff'
          : message.isUnread
          ? '#f8fafc'
          : '#ffffff',
        borderLeft: isSelected
          ? `4px solid ${MAIL_COLORS.primary}`
          : message.isUnread
          ? `4px solid ${MAIL_COLORS.unreadDot}`
          : '4px solid transparent',
        transition: 'all 0.15s ease-in-out',
        '&:hover': {
          bgcolor: isSelected ? '#eff8ff' : '#f1f5f9'
        }
      }}
    >
      {/* Аватар отправителя */}
      <Avatar
        sx={{
          width: 38,
          height: 38,
          fontSize: '0.85rem',
          fontWeight: 700,
          bgcolor: avatarColor,
          color: '#ffffff',
          flexShrink: 0
        }}
      >
        {initials}
      </Avatar>

      {/* Содержимое сообщения */}
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        {/* Верхняя строка: Отправитель и Дата */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mb: 0.25
          }}
        >
          <Typography
            sx={{
              fontWeight: message.isUnread ? 700 : 600,
              fontSize: '0.9rem',
              color: message.isUnread ? '#0f172a' : '#334155',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {senderName}
          </Typography>

          <Typography
            variant="caption"
            sx={{
              color: message.isUnread ? MAIL_COLORS.primary : '#94a3b8',
              fontWeight: message.isUnread ? 700 : 500,
              fontSize: '0.74rem',
              flexShrink: 0
            }}
          >
            {formattedDate}
          </Typography>
        </Box>

        {/* Тема письма */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.35 }}>
          <Typography
            sx={{
              fontWeight: message.isUnread ? 700 : 500,
              fontSize: '0.86rem',
              color: message.isUnread ? '#1e293b' : '#475569',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flexGrow: 1
            }}
          >
            {message.subject || '(Без темы)'}
          </Typography>

          {message.hasAttachment && (
            <AttachFile sx={{ fontSize: 16, color: '#94a3b8', transform: 'rotate(45deg)' }} />
          )}
        </Box>

        {/* Превью текста (сниппет) */}
        <Typography
          sx={{
            fontSize: '0.8rem',
            color: '#64748b',
            lineHeight: 1.35,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {message.snippet || ''}
        </Typography>
      </Box>
    </Box>
  );
};

export default MailListItem;
